import { PatientSessions } from "../../api/types";
import { Calendar, CheckCircle, Clock, X } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { patientAppointmentsQueryOptions } from "../../api/queries";
import { cn } from "@workspace/web-ui/lib/utils";
import { useSearch } from "@tanstack/react-router";

type SessionType = PatientSessions['sessions'][0];

interface AppointmentProps {
  selectedSession?: SessionType;
  userId: string;
}

interface AppointmentData {
  status: string;
  message: string | null;
  appointment_encounters?: {
    type: string;
    user_id: string;
    appointment_id: string;
    encounter_datetime: string;
    created_at: number;
    status?: string;
  }[];
}

export const Appointment = ({ selectedSession, userId }: AppointmentProps) => {
  // Get sessionId from route search params, similar to urgent-details-tabs
  const { sessionId } = useSearch({ strict: false }) as { sessionId?: string };
  const currentSessionId = sessionId || selectedSession?.session_id;

  const { data: appointmentData, isLoading } = useQuery({
    ...patientAppointmentsQueryOptions(userId, currentSessionId ?? "", "urgent-care"),
    enabled: !!currentSessionId
  });


  const getStatusConfig = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'upcoming':
      case 'scheduled':
        return {
          bgColor: 'bg-blue-100',
          textColor: 'text-blue-700',
          borderColor: 'border-blue-200',
          icon: Clock,
          iconColor: 'text-blue-500'
        };
      case 'completed':
        return {
          bgColor: 'bg-green-100',
          textColor: 'text-green-700',
          borderColor: 'border-green-200',
          icon: CheckCircle,
          iconColor: 'text-green-500'
        };
      case 'missed':
      case 'cancelled':
        return {
          bgColor: 'bg-gray-100',
          textColor: 'text-gray-700',
          borderColor: 'border-gray-200',
          icon: X,
          iconColor: 'text-gray-500'
        };
      default:
        return {
          bgColor: 'bg-orange-100',
          textColor: 'text-orange-700',
          borderColor: 'border-orange-200',
          icon: Calendar,
          iconColor: 'text-orange-500'
        };
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="bg-gray-50 rounded-lg p-4 animate-pulse">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                <div className="h-3 bg-gray-200 rounded w-1/2"></div>
              </div>
              <div className="h-6 bg-gray-200 rounded-full w-20"></div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  const appointments = (appointmentData as AppointmentData)?.appointment_encounters || [];

  if (appointments.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 px-4">
        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
          <Calendar className="w-8 h-8 text-gray-400" />
        </div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">No appointments yet</h3>
        <p className="text-sm text-gray-500 text-center max-w-md">
          When appointments are scheduled, they will appear here with their current status.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {appointments.map((appointment) => {
        const statusConfig = getStatusConfig(appointment.status || 'scheduled');
        const IconComponent = statusConfig.icon;
        const appointmentDate = new Date(appointment.encounter_datetime).toLocaleDateString();

        return (
          <div
            key={appointment.appointment_id}
            className="bg-gray-50 rounded-lg p-4 flex items-center justify-between"
          >
            <div className="flex-1">
              <h3 className="text-sm font-semibold text-gray-900 mb-1">
                Consultation Appointment
              </h3>
              <p className="text-sm text-gray-600">
                Date: {appointmentDate}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                ID: {appointment.appointment_id}
              </p>
            </div>
            <div className={cn(
              "flex items-center gap-2 px-3 py-1 rounded-full border",
              statusConfig.bgColor,
              statusConfig.borderColor
            )}>
              <IconComponent className={cn("w-4 h-4", statusConfig.iconColor)} />
              <span className={cn("text-xs font-medium capitalize", statusConfig.textColor)}>
                {appointment.status || 'Scheduled'}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}; 